import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import ConfirmationDialog from '../components/modals/ConfirmationDialog';

interface ConfirmationDialogContextType {
  showConfirmationDialog: (titleId: string, messageId: string, onConfirm: () => void, onCancel?: () => void) => void;
  closeConfirmationDialog: () => void;
}

const ConfirmationDialogContext = createContext<ConfirmationDialogContextType | undefined>(undefined);

interface ConfirmationDialogProviderProps {
  children: ReactNode;
}

const ConfirmationDialogProvider: React.FC<ConfirmationDialogProviderProps> = ({ children }): JSX.Element | null => {
  const [isOpen, setIsOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<{
    titleId: string;
    messageId: string;
    onConfirm: () => void;
    onCancel?: () => void; // Store it here
  } | null>(null);

  const showConfirmationDialog = useCallback((titleId: string, messageId: string, onConfirm: () => void, onCancel?: () => void) => {
    setDialogProps({ titleId, messageId, onConfirm, onCancel });
    setIsOpen(true);
  }, []);

  const closeConfirmationDialog = useCallback(() => {
    setIsOpen(false);
    setDialogProps(null);
  }, []);

  const handleCancel = () => {
    if (dialogProps?.onCancel) {
      dialogProps.onCancel();
    }
    closeConfirmationDialog();
  };

  return (
    <ConfirmationDialogContext.Provider value={{ showConfirmationDialog, closeConfirmationDialog }}>
      {children}
      {isOpen && dialogProps ? (
        <ConfirmationDialog
          open={isOpen}
          onClose={handleCancel} 
          onConfirm={() => {
            dialogProps.onConfirm();
            closeConfirmationDialog();
          }}
          titleId={dialogProps.titleId}
          messageId={dialogProps.messageId}
        />
      ) : null}
    </ConfirmationDialogContext.Provider>
  );
};

export const useConfirmationDialog = (): ConfirmationDialogContextType => {
  const context = useContext(ConfirmationDialogContext);
  if (!context) {
    throw new Error('useConfirmationDialog must be used within a ConfirmationDialogProvider');
  }
  return context;
};

export default ConfirmationDialogProvider;